<template>
    <div>
        <div class="d-flex align-center flex-wrap mb-2" style="gap: 12px;">
            <v-text-field v-model="search" dense outlined hide-details clearable prepend-inner-icon="mdi-magnify"
                placeholder="Search name / EMP no" style="max-width: 240px;"></v-text-field>
            <v-autocomplete v-model="sectionId" :items="sections" item-text="name" item-value="id"
                placeholder="All sections" dense outlined hide-details clearable style="max-width: 280px;"></v-autocomplete>
            <v-spacer></v-spacer>
            <span v-if="data" class="caption grey--text">
                {{ shown.length }} staff · {{ shownDays }} approved WFH day{{ shownDays === 1 ? '' : 's' }}
            </span>
        </div>

        <v-card outlined :loading="loading">
            <v-simple-table dense>
                <thead>
                    <tr class="text-uppercase">
                        <th>Staff</th>
                        <th>Section</th>
                        <th>WFH days</th>
                        <th>Approved</th>
                        <th width="1px"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="!loading && !shown.length">
                        <td colspan="5" class="text-center grey--text caption py-4">No approved WFH this month.</td>
                    </tr>
                    <tr v-if="shown.length">
                        <td colspan="5" class="caption grey--text py-1">
                            Green: worked from home · struck through: coming to the office (WFH given up) · orange: not used ·
                            <v-icon x-small>mdi-calendar-arrow-right</v-icon> moved at their request · "→ day?": change waiting for approval
                        </td>
                    </tr>
                    <tr v-for="s in shown" :key="s.staff_id">
                        <td class="py-1">
                            <div class="font-weight-medium">{{ s.name }}</div>
                            <div class="caption grey--text">{{ s.empno }}</div>
                        </td>
                        <td class="caption">{{ s.section }}<span v-if="s.unit && s.unit !== '-'"> / {{ s.unit }}</span></td>
                        <td>
                            <v-chip
                                v-for="d in s.days" :key="d.date"
                                x-small label class="mr-1 my-1"
                                :color="dayColor(d)" outlined
                                :title="dayTitle(d)"
                            >
                                <v-icon v-if="d.wfh_day.moved_from" x-small left>mdi-calendar-arrow-right</v-icon>
                                <span :class="{ 'text-decoration-line-through': d.wfh_day.state === 'office' }">{{ fmtDay(d.date) }}</span>
                                <span v-if="d.signed_in_at">&nbsp;· {{ d.signed_in_at }}–{{ d.signed_out_at || '…' }}</span>
                                <span v-else-if="d.wfh_day.state === 'office'">&nbsp;· office</span>
                                <span v-else-if="['unused', 'lapsed'].includes(d.wfh_day.state)">&nbsp;· not used</span>
                                <span v-if="d.wfh_day.move && d.wfh_day.move.open">&nbsp;→ {{ fmtDay(d.wfh_day.move.to) }}?</span>
                            </v-chip>
                        </td>
                        <td class="caption text-no-wrap">{{ s.approved_by }}<div class="grey--text">{{ s.approved_at }}</div></td>
                        <td><v-btn x-small text color="primary" @click="open(s)">Open</v-btn></td>
                    </tr>
                </tbody>
            </v-simple-table>
        </v-card>

        <v-dialog v-model="dialog.open" max-width="820px">
            <v-card v-if="dialog.staff">
                <v-card-title class="subtitle-1">
                    {{ dialog.staff.name }}
                    <span class="caption grey--text ml-2">{{ dialog.staff.section }}</span>
                    <v-spacer></v-spacer>
                    <v-btn icon small @click="dialog.open = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <month-schedule :year="year" :month="month" :staff-id="dialog.staff.staff_id" history @changed="load" />
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
// HR: every approved WFH day of the month, by staff member
// (MonthlyScheduleService::approvedWfh), with what became of it (`wfh_day`:
// worked, coming to the office, moved on request, not used). HR doesn't
// approve; "Open" lets HR change an approved plan (with a reason) where necessary.
import MonthSchedule from './MonthSchedule.vue'

export default {
    name: 'ApprovedWfh',
    components: { MonthSchedule },
    props: {
        year: { type: Number, required: true },
        month: { type: Number, required: true },
    },
    data: () => ({
        data: null,
        loading: false,
        search: '',
        sectionId: null,
        dialog: { open: false, staff: null },
    }),
    computed: {
        sections() {
            if (!this.data) return []
            const byId = {}
            this.data.staff.forEach(s => { byId[s.section_id] = s.section })
            return Object.entries(byId)
                .map(([id, name]) => ({ id: Number(id), name }))
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        },
        shown() {
            if (!this.data) return []
            const q = (this.search || '').trim().toLowerCase()
            return this.data.staff.filter(s =>
                (!this.sectionId || s.section_id === this.sectionId)
                && (!q || `${s.empno} ${s.name}`.toLowerCase().includes(q)))
        },
        shownDays() {
            return this.shown.reduce((n, s) => n + s.days.length, 0)
        },
    },
    watch: {
        year() { this.load() },
        month() { this.load() },
    },
    mounted() {
        this.load()
    },
    methods: {
        load() {
            this.loading = true
            this.$axios.get('/api/monthly-schedule/approved-wfh', { params: { year: this.year, month: this.month } })
                .then(resp => { this.data = resp.data })
                .finally(() => this.loading = false)
        },
        open(s) {
            this.dialog = { open: true, staff: s }
        },
        dayColor(d) {
            if (d.signed_in_at) return 'success'
            return { office: 'grey', unused: 'orange', lapsed: 'orange' }[d.wfh_day.state] || 'purple'
        },
        dayTitle(d) {
            const w = d.wfh_day
            const lines = [d.signed_in_at ? `Signed in ${d.signed_in_at}${d.signed_out_at ? ', out ' + d.signed_out_at : ''}`
                : { office: 'Coming to the office — WFH that week given up', unused: 'Not used', lapsed: "Not used — that week's WFH lapsed" }[w.state] || 'Not signed in yet']
            if (w.forgone_at) lines.push(`HR told ${w.forgone_at}${w.forgone_note ? ': ' + w.forgone_note : ''}`)
            if (w.moved_from) lines.push(`Moved from ${this.fmtDay(w.moved_from)}, approved by ${w.move_approved_by}`)
            if (w.move) lines.push(`${w.move.open ? 'Asked' : 'Asked (not approved in time)'} to move to ${this.fmtDay(w.move.to)}: ${w.move.reason}`)
            if (d.change_reason) lines.push(`${w.moved_from ? 'Reason' : 'Changed after approval'}: ${d.change_reason}`)
            return lines.join('\n')
        },
        fmtDay(s) {
            const [, m, d] = s.split('-')
            const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(`${s}T00:00:00`).getDay()]
            return `${day} ${d}/${m}`
        },
    },
}
</script>
